import type { GitRef, PlatformHubVersionControlSettingsResource, SpecifiedGitRef } from "@octopusdeploy/octopus-server-client";
import { toGitBranch } from "@octopusdeploy/octopus-server-client";
import { getValueInStorage, removeValueInStorage, setValueInStorage } from "~/hooks/localStorageHelpers";

interface StoredGitRef {
    GitRef: SpecifiedGitRef;
    Url: string;
    BasePath: string;
}

function isStoredGitRef(value: object): value is StoredGitRef {
    return "GitRef" in value && typeof value.GitRef === "string" && "Url" in value && typeof value.Url === "string" && "BasePath" in value && typeof value.BasePath === "string";
}

function parseJson(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
}

// Repositories can be referenced with or without a trailing slash or ".git" suffix, and hosts are
// case-insensitive. Normalise before comparing so cosmetic differences don't discard a valid, still-relevant entry.
function normalizeRepositoryUrl(url: string): string {
    return url
        .trim()
        .replace(/\.git\/?$/i, "")
        .replace(/\/$/, "")
        .toLowerCase();
}

function settingsMatch(stored: StoredGitRef, settings: PlatformHubVersionControlSettingsResource): boolean {
    return normalizeRepositoryUrl(stored.Url) === normalizeRepositoryUrl(settings.Url) && stored.BasePath === settings.BasePath;
}

function lastAccessedPlatformHubGitRefStore() {
    const LocalStorageKey = "octoLastAccessedPlatformHubGitRef";

    const buildKey = () => `${LocalStorageKey}`;

    const save = (gitRef: SpecifiedGitRef, gitPersistenceSettings: PlatformHubVersionControlSettingsResource): void => {
        const stored: StoredGitRef = { GitRef: gitRef, Url: gitPersistenceSettings.Url, BasePath: gitPersistenceSettings.BasePath };
        setValueInStorage(buildKey(), JSON.stringify(stored));
    };

    // Reads whatever is stored, discarding (and clearing) it if it was saved against a different Git
    // repository than the one Platform Hub currently points at. This can happen when the version control
    // settings are changed by something other than the portal, e.g. the API - see
    // https://github.com/OctopusDeploy/Issues/issues/10190.
    const get = (gitPersistenceSettings: PlatformHubVersionControlSettingsResource): GitRef => {
        const key = buildKey();
        const rawValue = getValueInStorage(key);

        if (rawValue) {
            const parsed = parseJson(rawValue);

            if (typeof parsed !== "object" || parsed === null || !isStoredGitRef(parsed)) {
                // Value predates this format - a bare GitRef string (or, if the stored value is corrupt, garbage)
                // with no repository fingerprint to check. Treat it as-is; it will be replaced with a
                // fingerprinted entry on the next save.
                return rawValue;
            }

            if (settingsMatch(parsed, gitPersistenceSettings)) {
                return parsed.GitRef;
            }

            // The remembered branch belongs to a repository Platform Hub no longer points at. Discard it
            // rather than asking the server to resolve it against the current repository.
            removeValueInStorage(key);
        }

        return toGitBranch(gitPersistenceSettings.DefaultBranch);
    };

    return {
        save,
        get,
    };
}

const lastAccessedPlatformHubGitRef = lastAccessedPlatformHubGitRefStore();

export { lastAccessedPlatformHubGitRef };
